// Copyright (C) 2026, Lifefinder Systems International AB

import React, { useEffect, useRef, useState } from 'react';

import { useGeofenceAreas } from '../../../context/GeofenceAreaContext';
import type { AreaAssignment, AreaShape, AssignedDevice, GeofenceAreaConfig } from '../../../types';
import { beaconAPI, checkinAPI, geofenceAreaConfigAPI } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SaveAreaData {
  id?: number;
  area_name: string;
  area_shape: AreaShape;
  position_mode: 'centroid' | 'custom';
  assignments: AreaAssignment[];
}

export interface AreaEditorProps {
  onClose: () => void;
  drawMode: 'circle' | 'rectangle' | 'polygon' | null;
  setDrawMode: (mode: 'circle' | 'rectangle' | 'polygon' | null) => void;
  pendingShape: AreaShape | null;
  /** Last map click from position-picking mode, cleared after consumption. */
  pendingPosition: [number, number] | null;
  onSave: (data: SaveAreaData) => Promise<void>;
  onDiscard: () => void;
  /** Ask the map to enter click-for-position mode. */
  onRequestPosition: () => void;
  onCancelPosition: () => void;
}

type EditorStep = 'list' | 'draw' | 'details' | 'assign' | 'position';

interface AvailableBeacon {
  id: number;
  device_id: string;
  name: string;
}

interface AvailableCheckin {
  id: number;
  device_id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Styles (reused across steps)
// ---------------------------------------------------------------------------

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  top: '10vh',
  right: '1vmin',
  width: 'clamp(260px, 22vw, 360px)',
  maxHeight: '80vh',
  overflowY: 'auto',
  background: '#fff',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
  zIndex: 1700,
  padding: '12px',
  fontFamily: 'sans-serif',
  fontSize: '14px',
};

const btnStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: '4px',
  border: '1px solid #aaa',
  background: '#f5f5f5',
  cursor: 'pointer',
  fontSize: '13px',
};

const btnActiveStyle: React.CSSProperties = {
  ...btnStyle,
  background: '#2563eb',
  color: '#fff',
  border: '1px solid #1d4ed8',
};

const btnPrimaryStyle: React.CSSProperties = {
  ...btnStyle,
  background: '#16a34a',
  color: '#fff',
  border: '1px solid #15803d',
};

const btnDangerStyle: React.CSSProperties = {
  ...btnStyle,
  color: '#c00',
  borderColor: '#c00',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: '6px',
  marginTop: '10px',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function shapeLabel(shape: AreaShape | null): string {
  if (!shape) return '–';
  if (shape.type === 'circle') return 'Cirkel';
  if (shape.type === 'rectangle') return 'Rektangel';
  return 'Frihand';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AreaEditor: React.FC<AreaEditorProps> = ({
  onClose,
  drawMode,
  setDrawMode,
  pendingShape,
  pendingPosition,
  onSave,
  onDiscard,
  onRequestPosition,
  onCancelPosition,
}) => {
  // Shared geofence-area configs — refreshed after a delete so the map overlay AND the
  // beacon/checkin marker-suppression set (both provider-backed) drop the removed area
  // instead of leaving its overlay drawn and its infrastructure markers hidden until reload.
  const { refresh: refreshAreaConfigs } = useGeofenceAreas();

  // ---- global state ----
  const [step, setStep] = useState<EditorStep>('list');
  const [configs, setConfigs] = useState<GeofenceAreaConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  // ---- per-edit state ----
  const [editingId, setEditingId] = useState<number | null>(null);
  const [currentShape, setCurrentShape] = useState<AreaShape | null>(null);
  const [areaName, setAreaName] = useState('');
  const [availableBeacons, setAvailableBeacons] = useState<AvailableBeacon[]>([]);
  const [availableCheckins, setAvailableCheckins] = useState<AvailableCheckin[]>([]);
  const [selectedBeaconIds, setSelectedBeaconIds] = useState<number[]>([]);
  const [selectedCheckinIds, setSelectedCheckinIds] = useState<number[]>([]);
  const [positionMode, setPositionMode] = useState<'centroid' | 'custom'>('centroid');
  const [customPositions, setCustomPositions] = useState<Record<string, [number, number]>>({});
  const [awaitingPositionKey, setAwaitingPositionKey] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  // ---- fetch area list ----
  const loadConfigs = () => {
    setLoadingConfigs(true);
    geofenceAreaConfigAPI
      .list()
      .then((data) => setConfigs(data.configs ?? []))
      .catch((err) => logger.error('AreaEditor: failed to load configs', err))
      .finally(() => setLoadingConfigs(false));
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  // Fetch available beacons and checkins once when entering the assign step.
  useEffect(() => {
    if (step !== 'assign') return;
    beaconAPI
      .getBeacons()
      .then((data: any) => {
        const list = data.beacons ?? [];
        setAvailableBeacons(
          list.map((b: any) => ({
            id: b.id,
            device_id: b.device_id ?? b.deveui,
            name: b.name ?? b.device_description ?? `Beacon ${b.id}`,
          }))
        );
      })
      .catch((err: unknown) => logger.error('AreaEditor: failed to load beacons', err));

    checkinAPI
      .getCheckins()
      .then((data: any) => {
        const list = data.checkins ?? [];
        setAvailableCheckins(
          list.map((c: any) => ({
            id: c.id,
            device_id: c.device_id ?? c.deveui,
            name: c.name ?? c.device_description ?? `Checkin ${c.id}`,
          }))
        );
      })
      .catch((err: unknown) => logger.error('AreaEditor: failed to load checkins', err));
  }, [step]);

  // Auto-advance from draw step when a shape is completed.
  useEffect(() => {
    if (step === 'draw' && pendingShape) {
      setCurrentShape(pendingShape);
      setStep('details');
    }
  }, [pendingShape, step]);

  // Capture map click when waiting for a device position.
  useEffect(() => {
    if (!pendingPosition || !awaitingPositionKey) return;
    setCustomPositions((prev) => ({ ...prev, [awaitingPositionKey]: pendingPosition }));
    setAwaitingPositionKey(null);
    onCancelPosition();
  }, [pendingPosition]);

  // Autofocus the name input when entering the details step.
  useEffect(() => {
    if (step === 'details') {
      setTimeout(() => nameInputRef.current?.focus(), 50);
    }
  }, [step]);

  // ---- helpers ----

  const resetEditorState = () => {
    setEditingId(null);
    setCurrentShape(null);
    setAreaName('');
    setSelectedBeaconIds([]);
    setSelectedCheckinIds([]);
    setPositionMode('centroid');
    setCustomPositions({});
    setAwaitingPositionKey(null);
    setDrawMode(null);
    onDiscard();
  };

  const startCreate = () => {
    resetEditorState();
    setStep('draw');
  };

  const startEdit = (cfg: GeofenceAreaConfig) => {
    setEditingId(cfg.id);
    setCurrentShape(cfg.area_shape);
    setAreaName(cfg.area_name);
    setSelectedBeaconIds(cfg.beacons.map((b) => b.id));
    setSelectedCheckinIds(cfg.checkins.map((c) => c.id));
    setPositionMode(cfg.position_mode);
    // Restore custom positions from existing beacon/checkin data.
    const pos: Record<string, [number, number]> = {};
    for (const b of cfg.beacons) {
      if (b.geofence_lat != null && b.geofence_lng != null) {
        pos[`beacon_${b.id}`] = [b.geofence_lat, b.geofence_lng];
      }
    }
    for (const c of cfg.checkins) {
      if (c.geofence_lat != null && c.geofence_lng != null) {
        pos[`checkin_${c.id}`] = [c.geofence_lat, c.geofence_lng];
      }
    }
    setCustomPositions(pos);
    setDrawMode(null);
    setStep('details');
  };

  const handleDelete = async (cfg: GeofenceAreaConfig) => {
    if (!window.confirm(`Ta bort "${cfg.area_name}"?`)) return;
    setDeleting(cfg.id);
    try {
      await geofenceAreaConfigAPI.delete(cfg.id);
      loadConfigs();
      refreshAreaConfigs(); // update the shared map overlay + marker-suppression set
    } catch (err) {
      logger.error('AreaEditor: delete failed', err);
    } finally {
      setDeleting(null);
    }
  };

  const handleSave = async () => {
    if (!currentShape || !areaName.trim()) return;

    const assignments: AreaAssignment[] = [
      ...selectedBeaconIds.map((id) => {
        const pos = customPositions[`beacon_${id}`];
        return {
          beacon_id: id,
          ...(positionMode === 'custom' && pos ? { lat: pos[0], lng: pos[1] } : {}),
        };
      }),
      ...selectedCheckinIds.map((id) => {
        const pos = customPositions[`checkin_${id}`];
        return {
          checkin_id: id,
          ...(positionMode === 'custom' && pos ? { lat: pos[0], lng: pos[1] } : {}),
        };
      }),
    ];

    setSaving(true);
    try {
      await onSave({
        id: editingId ?? undefined,
        area_name: areaName.trim(),
        area_shape: currentShape,
        position_mode: positionMode,
        assignments,
      });
      resetEditorState();
      loadConfigs();
      setStep('list');
    } catch (err) {
      logger.error('AreaEditor: save failed', err);
    } finally {
      setSaving(false);
    }
  };

  const allDevicesHavePosition = (): boolean => {
    if (positionMode === 'centroid') return true;
    const allBeacons = selectedBeaconIds.every((id) => customPositions[`beacon_${id}`]);
    const allCheckins = selectedCheckinIds.every((id) => customPositions[`checkin_${id}`]);
    return allBeacons && allCheckins;
  };

  // ---- device lists for position step ----
  // These are assembled from selected ids + names fetched in assign step.
  const selectedBeacons: AssignedDevice[] = selectedBeaconIds.map((id) => {
    const found = availableBeacons.find((b) => b.id === id);
    return {
      id,
      device_id: found?.device_id ?? '',
      name: found?.name ?? `Beacon ${id}`,
      geofence_lat: customPositions[`beacon_${id}`]?.[0] ?? null,
      geofence_lng: customPositions[`beacon_${id}`]?.[1] ?? null,
    };
  });

  const selectedCheckins: AssignedDevice[] = selectedCheckinIds.map((id) => {
    const found = availableCheckins.find((c) => c.id === id);
    return {
      id,
      device_id: found?.device_id ?? '',
      name: found?.name ?? `Checkin ${id}`,
      geofence_lat: customPositions[`checkin_${id}`]?.[0] ?? null,
      geofence_lng: customPositions[`checkin_${id}`]?.[1] ?? null,
    };
  });

  // ---- render ----

  const header = (title: string) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
      }}
    >
      <strong>{title}</strong>
      <button
        style={{ ...btnStyle, padding: '2px 8px' }}
        onClick={() => {
          resetEditorState();
          onClose();
        }}
      >
        ✕
      </button>
    </div>
  );

  // -- LIST --
  if (step === 'list') {
    return (
      <div style={panelStyle}>
        {header('Hantera områden')}
        {loadingConfigs && <div style={{ color: '#888' }}>Laddar…</div>}
        {!loadingConfigs && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #ddd' }}>
                  <th style={{ textAlign: 'left', padding: '4px 2px' }}>Namn</th>
                  <th style={{ textAlign: 'left', padding: '4px 2px' }}>Form</th>
                  <th style={{ textAlign: 'left', padding: '4px 2px' }}>Enheter</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {configs.map((cfg) => (
                  <tr key={cfg.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '4px 2px' }}>{cfg.area_name}</td>
                    <td style={{ padding: '4px 2px', color: cfg.area_shape ? '#333' : '#aaa' }}>
                      {shapeLabel(cfg.area_shape)}
                    </td>
                    <td style={{ padding: '4px 2px', color: '#555', fontSize: '12px' }}>
                      {cfg.beacons.length + cfg.checkins.length}
                    </td>
                    <td style={{ padding: '4px 2px', whiteSpace: 'nowrap' }}>
                      <button
                        style={{ ...btnStyle, marginRight: '4px' }}
                        onClick={() => startEdit(cfg)}
                      >
                        Redigera
                      </button>
                      <button
                        style={btnDangerStyle}
                        disabled={deleting === cfg.id}
                        onClick={() => handleDelete(cfg)}
                      >
                        {deleting === cfg.id ? '…' : '✕'}
                      </button>
                    </td>
                  </tr>
                ))}
                {configs.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ padding: '8px 2px', color: '#aaa', fontSize: '13px' }}>
                      Inga områden skapade
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ marginTop: '10px' }}>
              <button style={btnPrimaryStyle} onClick={startCreate}>
                + Skapa nytt område
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // -- DRAW --
  if (step === 'draw') {
    return (
      <div style={panelStyle}>
        {header('Rita område')}
        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#555' }}>
          Välj form och rita på kartan.
        </div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <button
            style={drawMode === 'circle' ? btnActiveStyle : btnStyle}
            onClick={() => setDrawMode('circle')}
          >
            ○ Cirkel
          </button>
          <button
            style={drawMode === 'rectangle' ? btnActiveStyle : btnStyle}
            onClick={() => setDrawMode('rectangle')}
          >
            ▭ Rektangel
          </button>
          <button
            style={drawMode === 'polygon' ? btnActiveStyle : btnStyle}
            onClick={() => setDrawMode('polygon')}
          >
            ⬡ Frihand
          </button>
        </div>
        {drawMode && (
          <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
            {drawMode === 'polygon'
              ? 'Klicka för att lägga till punkter. Dubbelklicka för att avsluta.'
              : 'Klicka för startpunkt, klicka igen för att slutföra.'}
          </div>
        )}
        {editingId && currentShape && (
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
            Befintlig form: {shapeLabel(currentShape)} — rita för att byta.
          </div>
        )}
        <div style={rowStyle}>
          <button
            style={btnStyle}
            onClick={() => {
              resetEditorState();
              setStep('list');
            }}
          >
            ← Avbryt
          </button>
          {editingId && currentShape && (
            <button style={btnStyle} onClick={() => setStep('details')}>
              Behåll form →
            </button>
          )}
        </div>
      </div>
    );
  }

  // -- DETAILS --
  if (step === 'details') {
    return (
      <div style={panelStyle}>
        {header('Namnge området')}
        <label style={{ fontSize: '13px', display: 'block', marginBottom: '6px' }}>
          Namn
          <input
            ref={nameInputRef}
            type="text"
            value={areaName}
            onChange={(e) => setAreaName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && areaName.trim()) setStep('assign');
            }}
            placeholder="t.ex. Maskinrum"
            style={{
              display: 'block',
              width: '100%',
              marginTop: '4px',
              padding: '6px 8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '13px',
              boxSizing: 'border-box',
            }}
          />
        </label>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
          Form: {shapeLabel(currentShape)}
          {' — '}
          <button
            style={{ ...btnStyle, fontSize: '11px', padding: '2px 6px' }}
            onClick={() => {
              onDiscard();
              setCurrentShape(null);
              setStep('draw');
            }}
          >
            Rita om
          </button>
        </div>
        <div style={rowStyle}>
          <button style={btnStyle} onClick={() => setStep(editingId ? 'list' : 'draw')}>
            ← Tillbaka
          </button>
          <button
            style={areaName.trim() ? btnActiveStyle : { ...btnStyle, opacity: 0.5 }}
            disabled={!areaName.trim()}
            onClick={() => setStep('assign')}
          >
            Nästa →
          </button>
        </div>
      </div>
    );
  }

  // -- ASSIGN --
  if (step === 'assign') {
    const toggleBeacon = (id: number) =>
      setSelectedBeaconIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    const toggleCheckin = (id: number) =>
      setSelectedCheckinIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    const anySelected = selectedBeaconIds.length + selectedCheckinIds.length > 0;

    return (
      <div style={panelStyle}>
        {header('Koppla beacons / checkins')}
        {availableBeacons.length === 0 && availableCheckins.length === 0 && (
          <div style={{ color: '#888', fontSize: '13px' }}>Laddar enheter…</div>
        )}
        {availableBeacons.length > 0 && (
          <>
            <div style={{ fontWeight: 600, fontSize: '12px', color: '#555', marginBottom: '4px' }}>
              Beacons
            </div>
            {availableBeacons.map((b) => (
              <label
                key={b.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '4px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedBeaconIds.includes(b.id)}
                  onChange={() => toggleBeacon(b.id)}
                />
                {b.name}
              </label>
            ))}
          </>
        )}
        {availableCheckins.length > 0 && (
          <>
            <div style={{ fontWeight: 600, fontSize: '12px', color: '#555', margin: '8px 0 4px' }}>
              Checkin-stationer
            </div>
            {availableCheckins.map((c) => (
              <label
                key={c.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '4px',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedCheckinIds.includes(c.id)}
                  onChange={() => toggleCheckin(c.id)}
                />
                {c.name}
              </label>
            ))}
          </>
        )}
        <div style={rowStyle}>
          <button style={btnStyle} onClick={() => setStep('details')}>
            ← Tillbaka
          </button>
          <button
            style={anySelected ? btnActiveStyle : { ...btnStyle, opacity: 0.5 }}
            disabled={!anySelected}
            onClick={() => setStep('position')}
          >
            Nästa →
          </button>
        </div>
      </div>
    );
  }

  // -- POSITION --
  if (step === 'position') {
    const allDevices = [
      ...selectedBeacons,
      ...selectedCheckins.map((c) => ({ ...c, _isCheckin: true })),
    ];

    return (
      <div style={panelStyle}>
        {header('Positionering')}
        <div style={{ marginBottom: '8px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '6px',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              checked={positionMode === 'centroid'}
              onChange={() => setPositionMode('centroid')}
            />
            Centroid (automatisk)
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <input
              type="radio"
              checked={positionMode === 'custom'}
              onChange={() => setPositionMode('custom')}
            />
            Manuell — klicka ut per enhet
          </label>
        </div>

        {positionMode === 'custom' && (
          <div style={{ marginTop: '8px' }}>
            {selectedBeaconIds.map((id) => {
              const key = `beacon_${id}`;
              const name = availableBeacons.find((b) => b.id === id)?.name ?? `Beacon ${id}`;
              const pos = customPositions[key];
              const isAwaiting = awaitingPositionKey === key;
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontSize: '13px',
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {name}
                  </span>
                  {pos ? (
                    <span style={{ color: '#16a34a', marginLeft: '6px', whiteSpace: 'nowrap' }}>
                      ✓{' '}
                      <button
                        style={{ ...btnStyle, fontSize: '11px', padding: '1px 5px' }}
                        onClick={() => {
                          setAwaitingPositionKey(key);
                          onRequestPosition();
                        }}
                      >
                        Ändra
                      </button>
                    </span>
                  ) : (
                    <button
                      style={
                        isAwaiting
                          ? { ...btnActiveStyle, fontSize: '12px' }
                          : { ...btnStyle, fontSize: '12px' }
                      }
                      onClick={() => {
                        setAwaitingPositionKey(key);
                        onRequestPosition();
                      }}
                    >
                      {isAwaiting ? 'Klicka på kartan…' : 'Välj position'}
                    </button>
                  )}
                </div>
              );
            })}
            {selectedCheckinIds.map((id) => {
              const key = `checkin_${id}`;
              const name = availableCheckins.find((c) => c.id === id)?.name ?? `Checkin ${id}`;
              const pos = customPositions[key];
              const isAwaiting = awaitingPositionKey === key;
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                    fontSize: '13px',
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {name}
                  </span>
                  {pos ? (
                    <span style={{ color: '#16a34a', marginLeft: '6px', whiteSpace: 'nowrap' }}>
                      ✓{' '}
                      <button
                        style={{ ...btnStyle, fontSize: '11px', padding: '1px 5px' }}
                        onClick={() => {
                          setAwaitingPositionKey(key);
                          onRequestPosition();
                        }}
                      >
                        Ändra
                      </button>
                    </span>
                  ) : (
                    <button
                      style={
                        isAwaiting
                          ? { ...btnActiveStyle, fontSize: '12px' }
                          : { ...btnStyle, fontSize: '12px' }
                      }
                      onClick={() => {
                        setAwaitingPositionKey(key);
                        onRequestPosition();
                      }}
                    >
                      {isAwaiting ? 'Klicka på kartan…' : 'Välj position'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={rowStyle}>
          <button style={btnStyle} onClick={() => setStep('assign')}>
            ← Tillbaka
          </button>
          <button
            style={allDevicesHavePosition() ? btnPrimaryStyle : { ...btnStyle, opacity: 0.5 }}
            disabled={!allDevicesHavePosition() || saving}
            onClick={handleSave}
          >
            {saving ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AreaEditor;
